import React, { useRef } from 'react';
import { htmlDecode } from '../helpers/functions';
import { useInView } from 'react-intersection-observer';
import {
  motion,
  useTransform,
  useViewportScroll,
  AnimatePresence,
  useMotionValue,
  useAnimation,
} from 'framer-motion';
import Slider from 'react-slick';
import { SlickArrowLeft, SlickArrowRight } from './Slider';
import { useRefScrollProgress, useIsScreenSize } from '../helpers/hooks';
import cx from 'classnames';
import styles from './HomeBlocks.module.scss';

import 'slick-carousel/slick/slick.css';

export const HomeBlocks = ({
  variant,
  title,
  subTitle,
  content,
  backgroundImage,
  primaryImage,
  wrapperClassBlock,
  button,
  locations,
  iconBlock,
  photoGallery,
}) => {
  const ref = React.useRef();
  const [inViewRef, inView] = useInView({ threshold: 0 });
  const { start, end } = useRefScrollProgress(ref);
  const { scrollY, scrollYProgress } = useViewportScroll();
  const locationBlocks = JSON.parse(htmlDecode(locations));
  const iconBlocks = JSON.parse(htmlDecode(iconBlock));
  const isTablet = useIsScreenSize('md');

  let parallaxRange = [0, 0];
  let parallaxRangeSecondary = [-150, 15];

  const yParallax = useTransform(
    scrollYProgress,
    [start * 0.05, end],
    parallaxRange
  );

  const yParallaxSecondary = useTransform(
    scrollYProgress,
    [start * 0.05, end],
    parallaxRangeSecondary
  );

  const setRefs = React.useCallback(
    (node) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  // Block Mapping Guide
  // 0 : Hero Banner
  // 1 : Intro
  // 2 : Locations
  // 3 : Icon Blocks
  // 4 : Call Out
  // 5 : Photo Gallery

  const wrapperClass = {
    default: `bg-no-repeat`,
    1: `pt-16 pb-8 bg-right-bottom`,
    2: `bg-cover py-16`,
    3: `py-16`,
    4: `bg-cover py-16 px-4 lg:px-0`,
    5: `text-center py-16`,
  };

  const containerClass = {
    default: `container`,
    1: `flex flex-wrap md:flex-nowrap px-4 xl:px-0 pb-12 md:pb-48`,
    2: ``,
  };

  const primaryImageClass = {
    default: `lazyload`,
    0: `absolute inset-0 m-auto z-20 flex items-center justify-center`,
    1: `w-full md:w-5/12 order-last md:order-first flex justify-end relative`,
    4: `w-full sm:w-1/2`,
    6: `w-full sm:w-1/2`,
    img: {
      default: `relative z-10`,
      0: `transform -translate-x-12 xl:-translate-x-1/3 max-h-28 lg:max-h-full max-w-full`,
      1: `md:max-w-7xl md:absolute top-0 right-0`,
    },
  };

  const secondaryImageClass = {
    default: ``,
    img: {
      default: `relative z-10`,
    },
  };

  const contentWrapper = {
    default: ``,
    1: `w-full sm:w-7/12 order-first md:order-last mb-8 sm:mb-0`,
    4: `text-white`,
    6: `w-full sm:w-1/2 pr-0 sm:pr-12 mb-8 sm:mb-0`,
  };

  const titleBoxClass = {
    default: `pl-4 border-l-8 border-primary leading-4 pt-2 ml-4 xl:ml-0`,
  };

  const titleClass = {
    default: ``,
    1: ``,
    2: `text-center mb-12`,
    3: `text-center mb-12`,
    4: `text-white`,
  };

  const subTitleClass = {
    default: `mb-0 text-content font-normal leading-6`,
  };

  const contentClass = {
    default: `relative z-10`,
    1: `md:text-right md:pl-16`,
  };

  const buttonClass = {
    default: `mt-6`,
    3: `text-center mt-4`,
  };
  return (
    <div
      className={cx(
        wrapperClass.default,
        wrapperClass[variant],
        styles[`block-${variant}`],
        wrapperClassBlock
      )}
      style={{
        backgroundImage:
          backgroundImage && variant !== '0'
            ? `url(${backgroundImage.url})`
            : `none`,
      }}
      ref={setRefs}
    >
      <div>
        {variant === '0' && (
          <div className="relative">
            <picture className="relative z-10">
              <img
                src={backgroundImage.url}
                alt={backgroundImage.alt}
                className="w-full"
              />
            </picture>
            {primaryImage && (
              <picture
                className={cx(
                  primaryImageClass.default,
                  primaryImageClass[variant]
                )}
              >
                <img
                  src={primaryImage.url}
                  alt={primaryImage.alt}
                  className={cx(
                    primaryImageClass.img.default,
                    primaryImageClass.img[variant]
                  )}
                />
              </picture>
            )}
          </div>
        )}
        {title || content ? (
          <div
            className={cx(
              containerClass.default,
              containerClass[variant],
              styles[`content_wrapper_${variant}`]
            )}
          >
            {primaryImage && (
              <picture
                className={cx(
                  primaryImageClass.default,
                  primaryImageClass[variant]
                )}
              >
                <img
                  src={primaryImage.url}
                  alt={primaryImage.alt}
                  className={cx(
                    primaryImageClass.img.default,
                    primaryImageClass.img[variant]
                  )}
                />
              </picture>
            )}
            <div
              className={cx(contentWrapper.default, contentWrapper[variant])}
            >
              {title && !subTitle && (
                <h2
                  className={cx(titleClass.default, titleClass[variant])}
                  dangerouslySetInnerHTML={{ __html: htmlDecode(title) }}
                />
              )}
              {title && subTitle && (
                <div
                  className={cx(titleBoxClass.default, titleBoxClass[variant])}
                >
                  <h4
                    className={cx(
                      subTitleClass.default,
                      subTitleClass[variant]
                    )}
                    dangerouslySetInnerHTML={{ __html: htmlDecode(subTitle) }}
                  />
                  <h2
                    className={cx(titleClass.default, titleClass[variant])}
                    dangerouslySetInnerHTML={{ __html: htmlDecode(title) }}
                  />
                </div>
              )}
              {content && (
                <div
                  className={cx(contentClass.default, contentClass[variant])}
                  dangerouslySetInnerHTML={{ __html: htmlDecode(content) }}
                />
              )}
              {iconBlocks && <IconBlocks blocks={iconBlocks} />}
              {button && (
                <div className={cx(buttonClass.default, buttonClass[variant])}>
                  <a
                    href={button.url}
                    className="btn"
                    dangerouslySetInnerHTML={{
                      __html: htmlDecode(button.label),
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : null}

        {locationBlocks && <LocationBlocks blocks={locationBlocks} />}
        {photoGallery && <PhotoGallery blocks={photoGallery} />}
      </div>
    </div>
  );
};
function LocationBlocks({ blocks }) {
  return (
    <div className="flex flex-wrap justify-center">
      {blocks.map((block, key) => (
        <div
          key={key}
          className="w-full sm:w-1/2 lg:w-4/12 px-4 mb-8 xl:px-0 text-white"
        >
          {block.thumbnail && (
            <img
              src={block.thumbnail.url}
              alt={block.thumbnail.alt}
              className="lazyload block mx-auto"
            />
          )}
          <div className="p-4 text-center">
            {block.title && (
              <h3
                dangerouslySetInnerHTML={{ __html: block.title }}
                className={cx(`text-white`)}
              />
            )}
            {block.address && (
              <div
                dangerouslySetInnerHTML={{ __html: block.address }}
                className={cx(`text-white`)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PhotoGallery({ blocks }) {
  return (
    <div className="flex items-center justify-center">
      {blocks.map((block, key) => (
        <div key={key} className="w-full sm:w-1/2 lg:w-4/12 px-4 mb-8 xl:px-0">
          <img
            src={block.url}
            alt={block.alt}
            className="lazyload block mx-auto"
          />
        </div>
      ))}
    </div>
  );
}

function IconBlocks({ blocks }) {
  return (
    <div className="flex flex-wrap justify-center">
      {blocks.map((block, key) => (
        <div key={key} className="w-1/2 sm:w-4/12 px-4 xl:px-0 mb-8">
          {block.thumbnail && (
            <img
              src={block.thumbnail.url}
              alt={block.thumbnail.alt}
              className="lazyload block mx-auto"
            />
          )}
          <div className="p-4 text-center">
            {block.title && (
              <h3
                dangerouslySetInnerHTML={{ __html: htmlDecode(block.title) }}
                className={cx(``)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
